import { useEffect, useState } from "react";
import { feedProductionService } from "@/services/feedProductionService";

// The day's feed batches checked against the silo BEFORE submit. Milling more
// than is in stock is allowed — the delivery may simply not be entered yet —
// but the supervisor used to learn it only from the results screen ("Soya DOC
// stock is now -38.1 kg", live walk on QA, 2026-10-31), after it was saved.
//
// Checked here on Review, not on Feed Prepared: the day's deliveries are typed
// on the step AFTER it, and submit saves them first, so they count as stock.

export interface PlannedBatch {
  formula_id: number;
  batch_size_kg: number;
}

interface Short {
  id: number;
  name: string;
  unit: string;
  need: number;
  have: number;
}

interface Props {
  batches: PlannedBatch[];
  /** Quantity received today per raw material id, from the Raw Materials step. */
  received: Record<number, number>;
}

const fmt = (n: number) =>
  (Math.round(n * 10) / 10 || 0).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  });
/** "300 kg", or "300" for a material with no unit on record. */
const qty = (n: number, unit: string) => `${fmt(n)}${unit ? ` ${unit}` : ""}`;

export default function StockShortfall({ batches, received }: Props) {
  const [shorts, setShorts] = useState<Short[]>([]);
  // How many batches could not be checked (a formula with no items, a network
  // failure). The others are still checked: one bad preview must not hide a
  // shortage another batch shows.
  const [failed, setFailed] = useState(0);
  // One key for the inputs, so the check reruns only when they change.
  const key = JSON.stringify([batches, received]);

  useEffect(() => {
    let current = true;
    setFailed(0);
    setShorts([]);
    if (batches.length === 0) return;
    Promise.allSettled(
      batches.map((b) =>
        feedProductionService.preview(b.formula_id, b.batch_size_kg),
      ),
    ).then((settled) => {
      if (!current) return;
      const plans = settled.flatMap((r) =>
        r.status === "fulfilled" ? [r.value] : [],
      );
      setFailed(settled.length - plans.length);
      // Two batches can draw on one material: add up what they need, and
      // compare with one stock figure (every preview reads the same silo).
      const need = new Map<number, Short>();
      for (const plan of plans) {
        for (const l of plan.lines) {
          const s = need.get(l.raw_material_id);
          if (s) s.need += l.qty;
          else
            need.set(l.raw_material_id, {
              id: l.raw_material_id,
              name: l.name,
              unit: l.unit,
              need: l.qty,
              have: l.in_stock + (received[l.raw_material_id] ?? 0),
            });
        }
      }
      // Short by at least what the screen can show (0.1), so it never reads
      // "need 300, have 300" from float noise.
      setShorts([...need.values()].filter((s) => s.need - s.have >= 0.05));
    });
    return () => {
      current = false;
    };
    // `key` stands for both inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const notChecked = failed > 0 && (
    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
      {failed === batches.length
        ? "The feed batches"
        : `${failed} of the feed batches`}{" "}
      could not be checked against stock. Submitting still saves them.
    </p>
  );
  if (shorts.length === 0) return notChecked || null;
  return (
    <>
      <div
        role="alert"
        data-testid="stock-shortfall"
        className="mt-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-2.5 text-xs text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400"
      >
        <p className="mb-1 font-medium">
          Not enough in stock for these batches:
        </p>
        <ul className="mb-1 space-y-0.5">
          {shorts.map((s) => (
            <li key={s.id}>
              {s.name}: need {qty(s.need, s.unit)}, have {qty(s.have, s.unit)} —
              stock would go to {qty(s.have - s.need, s.unit)}
            </li>
          ))}
        </ul>
        <p>
          If a delivery came today, go Back and enter it under Raw Materials.
          Otherwise check the batch size.
        </p>
      </div>
      {notChecked}
    </>
  );
}
